/* eslint-disable no-param-reassign */
import { uniqueId } from "lodash";

import { getPRPC, GetPRPC } from "../../hooks/usePRPC";
import createRPCQuery from "../../utils/createRPCQuery.util";
import createLogger from "../../utils/logger.util";

type SubscribeRequestFn<T> = (prpc: GetPRPC) => Promise<T>;

interface BaseSubscribeResponse {
	unsubscribe: () => Promise<void>;
	update: (...args: any[]) => Promise<void>;
}

interface Subscription<
	TResponse extends BaseSubscribeResponse = BaseSubscribeResponse,
> {
	/**
	 * Unique id of the subscription.
	 */
	id: string;

	/**
	 * Name of the subscription.
	 */
	name?: string;

	/**
	 * Additional options for the subscription.
	 */
	options?: SubscriptionAddOptions;

	/**
	 * Session id of the prpc session.
	 */
	sessionId: string;

	/**
	 * Creation timestamp.
	 */
	timestamp: number;

	/**
	 * Last reconnection timestamp.
	 */
	reconnectedAt?: number;

	/**
	 * Number of times the subscription has been reconnected.
	 */
	reconnectedTimes: number;

	/**
	 * The request function to subscribe to.
	 */
	requestFunction: (prpc: GetPRPC) => Promise<TResponse>;

	/**
	 * Actual response of the request function.
	 */
	requestFunctionResponse: TResponse;
}

interface SubscriptionAddOptions {
	name?: string;
	metadata?: Record<string, any>;

	/**
	 * Whether to throw an error if the prpc session is not available.
	 * @default false
	 */
	throwOnError?: boolean;
}

const prepareResponse = async (handler: Promise<any>) => {
	const response = await handler;
	if ("status" in response && "data" in response) {
		return createRPCQuery(() => response);
	}
	return response;
};

/**
 * Subscription pool used to manage subscriptions.
 *
 * Stores all subscriptions and reconnects them when the connection is lost.
 * Also wraps subscriptions to automatically use a new PRPC session.
 *
 * @example
 * ```ts
 * const subscription = await SubscriptionPool.add((prpc) => prpc.theirsModel.example.subscribe({...});
 * // Subscription initialized and ready to use
 *
 * // Later, when the connection is lost
 * await SubscriptionPool.onReconnect(newPrpcSession); // Reconnects all subscriptions
 *
 * await subscription.update(...); // Updates the new subscription with the new prpc session
 * await subscription.unsubscribe(); // Unsubscribes from the subscription and removes it from the pool
 * ```
 */
class SubscriptionPool {
	private static logger = createLogger({ name: "SubscriptionPool" });

	/**
	 * List of subscriptions that are currently active.
	 */
	public static subscriptions: Subscription[] = [];

	/**
	 * Adds a subscription to the pool.
	 * @param request The request function to subscribe to.
	 * @param name The name of the subscription. Used for debugging purposes.
	 * @returns The wrapped subscribe's response or null if the prpc session is not available.
	 */
	public static async add<T extends BaseSubscribeResponse>(
		request: SubscribeRequestFn<T>,
		options: SubscriptionAddOptions = {},
	) {
		const currentPrpcSession = getPRPC();

		if (!currentPrpcSession) {
			throw new Error(
				"PRPC Session is not available. Seems like you are not connected to the server.",
			);
		}

		try {
			const response = await prepareResponse(request(currentPrpcSession));
			const id = uniqueId("subscription-");
			const { sessionId } = currentPrpcSession;

			this.logger.info(
				`Add subscription to pool '${id}', sessionId=${sessionId}`,
			);

			// Subscribed successfully, add to the list of subscriptions
			// Wrap the unsubscribe and other functions to avoid errors from the old prpc session

			const subscription: Subscription = {
				id,
				sessionId,
				name: options?.name,
				options,
				reconnectedTimes: 0,
				timestamp: Date.now(),
				requestFunction: request,
				requestFunctionResponse: response,
			};

			this.subscriptions.push(subscription);

			return this.wrapSubscription(subscription);
		} catch (error) {
			this.logger.error("Failed to add subscription to pool", error);
			throw error;
		}
	}

	/**
	 * Starts monitoring the active subscriptions at a specified interval.
	 * @param ms The interval in milliseconds. Default is 10,000 milliseconds (10 seconds).
	 */
	public static startMonitor(ms = 10_000) {
		setInterval(() => {
			this.logger.info(
				`Active subscriptions (${this.subscriptions.length}):`,
				this.subscriptions.map((item) => ({
					id: item.id,
					name: item.name,
					options: item.options,
					sessionId: item.sessionId,
					timestamp: item.timestamp,
					reconnectedAt: item.reconnectedAt,
					reconnectedTimes: item.reconnectedTimes,
				})),
			);
		}, ms);
	}

	/**
	 * Clears all subscriptions in the pool.
	 */
	public static async clear() {
		this.logger.info(
			"Subscriptions clearing, count:",
			this.subscriptions.length,
		);
		this.subscriptions = [];
	}

	/**
	 * Reconnects all subscriptions in the pool.
	 * @param prpc New prpc session object.
	 */
	public static async onReconnect(prpc: GetPRPC) {
		this.logger.info(
			`Received reconnect event (subscriptions=${this.subscriptions.length}), new session id:`,
			prpc.sessionId,
		);
		const promise = Promise.all(
			this.subscriptions.map(async (subscription) => {
				const response = await subscription.requestFunction(prpc);

				// Unsubscribed while re-subscribing: the old response was already
				// dead, so close the new server subscription here.
				if (!this.subscriptions.includes(subscription)) {
					this.safeUnsubscribe(response, subscription);
					return;
				}

				// The session id and the response change together: until then the
				// subscription belongs to the old session and `unsubscribe` skips
				// the dead response instead of calling it.
				subscription.sessionId = prpc.sessionId;
				subscription.requestFunctionResponse = response;
				subscription.reconnectedAt = Date.now();
				subscription.reconnectedTimes++;
			}),
		);

		return promise
			.catch((error) => this.logger.error("Failed to reconnect", error))
			.then(() => {
				this.logger.info(
					`Reconnected subscriptions (${this.subscriptions.length}):`,
					this.subscriptions.map((item) => item.name),
				);
			});
	}

	/**
	 * Wraps a subscription object and adds additional functions to it based on the response object.
	 * @param subscription - The subscription object to wrap.
	 * @returns The wrapped subscription object with additional functions.
	 */
	protected static wrapSubscription<T extends BaseSubscribeResponse>(
		subscription: Subscription<T>,
	): T {
		const response = { ...subscription.requestFunctionResponse };
		const functions = Object.keys(response).reduce((acc, key) => {
			const value = response[key];
			if (typeof value !== "function") return acc;
			if (key === "unsubscribe") {
				acc[key] = this.wrapUnsubscribeFunction(subscription.id);
			} else {
				acc[key] = this.wrapAnyFunction(subscription, key);
			}
			return acc;
		}, response as any);

		return {
			...response,
			...functions,
		};
	}

	/**
	 * Closes a server subscription nobody holds any more; errors are only
	 * logged.
	 */
	/**
	 * A closed session (reconnect) already dropped the server side: expected,
	 * debug only; any other unsubscribe error stays a warning (dev check G5).
	 */
	protected static logUnsubscribeError(id: string, error: unknown) {
		const message = (error as Error)?.message ?? "";
		const log = message.includes("Session is not Opened")
			? this.logger.debug
			: this.logger.warn;
		log.call(
			this.logger,
			`Failed to unsubscribe from subscription (${id})`,
			error,
		);
	}

	protected static safeUnsubscribe(
		response: BaseSubscribeResponse | undefined,
		subscription: Subscription<any>,
	) {
		try {
			Promise.resolve(response?.unsubscribe?.()).catch((error) =>
				this.logUnsubscribeError(subscription.id, error),
			);
		} catch (error) {
			this.logUnsubscribeError(subscription.id, error);
		}
	}

	/**
	 * Wraps the unsubscribe function for a subscription with the given ID.
	 * @param id - The ID of the subscription.
	 * @returns The wrapped unsubscribe function.
	 */
	protected static wrapUnsubscribeFunction(id: string) {
		// Always a promise: callers chain `.catch` on it (Sector.subscribeRealtime)
		// and a `null` there throws in an effect cleanup.
		const done = () => Promise.resolve(null);
		return () => {
			const index = this.subscriptions.findIndex((s) => s.id === id);
			if (index === -1) return done();

			const subscription = this.subscriptions[index];
			this.logger.info(
				`Unsubscribing from subscription (${id})`,
				subscription.name,
			);
			this.subscriptions.splice(index, 1);

			// `onReconnect` re-subscribes on the new session and swaps both
			// `sessionId` and `requestFunctionResponse`, so the stored response is
			// callable whenever the subscription still belongs to the live session.
			// A stale session means the server side is already gone.
			if (getPRPC()?.sessionId !== subscription.sessionId) return done();

			// Callers unsubscribe from effect cleanups: a throw there unmounts the
			// whole app (white screen after a reconnect), so it never leaves here.
			const response = subscription.requestFunctionResponse;
			if (typeof response?.unsubscribe !== "function") {
				// Not subscribed yet (reconnect in progress): nothing to close.
				this.logger.debug(
					`No unsubscribe function for subscription (${id})`,
				);
				return done();
			}

			let pending: Promise<unknown>;
			try {
				pending = Promise.resolve(response.unsubscribe());
			} catch (error) {
				pending = Promise.reject(error);
			}

			return pending
				.catch((error) => {
					if (subscription?.options?.throwOnError) throw error;
					this.logUnsubscribeError(id, error);
				})
				.finally(() => {
					this.logger.info(
						`Successfully unsubscribed from subscription (${id})`,
					);
				});
		};
	}

	/**
	 * Wraps a subscription function and returns a new function that can be called with any arguments.
	 * If the wrapped function is not a function, it returns the original value.
	 * @param subscription - The subscription object.
	 * @param key - The key of the function to wrap.
	 * @returns A new function that can be called with any arguments.
	 */
	protected static wrapAnyFunction<T extends BaseSubscribeResponse>(
		subscription: Subscription<T>,
		key: string,
	) {
		return (...args: any[]) => {
			const { requestFunctionResponse } = subscription;
			const value = requestFunctionResponse[key];
			if (typeof value !== "function") return value;
			return value(...args);
		};
	}
}

SubscriptionPool.startMonitor();

export default SubscriptionPool;
